import { breakpoints, classes } from '@theme/constants';
import { gsap } from '@theme/gsap';
import { HTMLCustomElement } from '@theme/html-custom-element';

class ProductGuide extends HTMLCustomElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    gsap.matchMedia().add(`(min-width: ${breakpoints.large}px)`, () => {
      if (!this.scrollConfigured) {
        this.scrollConfigured = true;

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: this,
            pin: true,
            scrub: true,
            start: () => `-=${parseInt(getComputedStyle(document.body).getPropertyValue('--header-height')) ?? 0}`,
            end: () => `+=${this.getScrollEnd() + 500}`,
            anticipatePin: 1,
            onEnter: ({ refresh }) => {
              this.classList.add(classes.active);

              requestAnimationFrame(() => {
                refresh(true);
              });
            },

            onEnterBack: ({ refresh }) => {
              this.classList.add(classes.active);

              requestAnimationFrame(() => {
                refresh(true);
              });
            },

            onLeave: ({ refresh }) => {
              this.refs.sidebar.scrollTo({ y: 0 });

              this.classList.remove(classes.active);

              requestAnimationFrame(() => {
                refresh();
              });
            },
          },
        });

        timeline.to(this.refs.steps, {
          ease: 'none',
          x: () => -this.getScrollEnd(),
        });
      }

      return () => {
        this.scrollConfigured = false;
      };
    });
  }

  getScrollEnd() {
    return this.refs.steps.scrollWidth - this.refs.steps.parentElement.offsetWidth + 20;
  }
}

class ProductGuideStepProducts extends HTMLCustomElement {
  activeIndex = 0;

  productResizeObserver = new ResizeObserver(([entry]) => {
    entry.target.parentElement.style.height = `${entry.target.offsetHeight}px`;
  });

  /**
   * @returns {import('@theme/toggle-element').ToggleElement[]}
   */
  get quickAddOptions() {
    return this.querySelectorAll('.quick-add-options');
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.show(this.activeIndex);
    this.autoplay();

    if (this.refs.bullet) {
      this.on('click', this.refs.bullet.all, this.handleBulletClick.bind(this));
    }

    if (this.quickAddOptions.length) {
      this.on('open', [...this.quickAddOptions], (event) => {
        if (this.interval) {
          clearInterval(this.interval);
        }

        this.on('close', event.target, this.autoplay.bind(this), { once: true });
      });
    }
  }

  /**
   * Handles the bullet click event.
   * @param {MouseEvent} event
   */
  handleBulletClick(event) {
    this.show(parseInt(event.currentTarget.dataset.index0));
    this.autoplay();
  }

  /**
   * Runs the autoplay.
   */
  autoplay() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    this.interval = setInterval(() => {
      const index = this.activeIndex + 1;

      this.show(index === this.refs.product.all.length ? 0 : index);
    }, 5000);
  }

  /**
   * Shows the product at the provided index.
   * @param {number} index
   */
  show(index) {
    for (const element of this.quickAddOptions) {
      element.close();
    }

    if (this.refs.product) {
      for (const product of this.refs.product.all) {
        const isActive = index === parseInt(product.dataset.index0);

        if (isActive) {
          product.classList.add(classes.active);

          requestAnimationFrame(() => {
            product.parentElement.style.height = `${product.offsetHeight}px`;
          });

          this.productResizeObserver.disconnect();
          this.productResizeObserver.observe(product);

          continue;
        }

        product.classList.remove(classes.active);
      }
    }

    if (this.refs.bullet) {
      for (const bullet of this.refs.bullet.all) {
        bullet.classList.toggle(classes.active, index === parseInt(bullet.dataset.index0));
      }
    }

    this.activeIndex = index;
  }
}

if (!customElements.get('product-guide')) customElements.define('product-guide', ProductGuide);
if (!customElements.get('product-guide-step-products'))
  customElements.define('product-guide-step-products', ProductGuideStepProducts);
